import { generateCLIAccessToken } from "api/personalAccessTokens";
import { Button } from "components/ui/legacy/Button";
import { Typography } from "components/ui/legacy/Typography";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { getErrorTexts } from "utils/errors/apiError";

export const CLITokenGenerationPage = () => {
  const [queryParams] = useSearchParams();
  const [done, setDone] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();

  const portParam = queryParams.get("port");
  const securedParam = queryParams.get("secured");
  const port = Number(portParam);
  const isPortValid = Boolean(portParam) && !isNaN(port);

  const handleGenerate = useCallback(async () => {
    if (!isPortValid || generating || done) {
      return;
    }
    setGenerating(true);
    setErrorMessage("");
    try {
      await generateCLIAccessToken(port, securedParam);
      setDone(true);
    } catch (error) {
      setErrorMessage(getErrorTexts(error as Error).message || t("errors.unknown.message"));
    } finally {
      setGenerating(false);
    }
  }, [done, generating, isPortValid, port, securedParam, t]);

  const renderContent = () => {
    if (!isPortValid) {
      return (
        <Typography data-testid="cli-token-generation-error" variant="standard">
          {t("pages.cliTokenGeneration.invalidPort")}
        </Typography>
      );
    }

    if (done) {
      return (
        <Typography data-testid="cli-token-generation-done" variant="standard">
          {t("pages.cliTokenGeneration.done")}
        </Typography>
      );
    }

    return (
      <>
        <Typography variant="h1">{t("pages.cliTokenGeneration.title")}</Typography>
        <Typography className="hyphens-none!" data-testid="cli-token-generation-description" variant="standard">
          {t("pages.cliTokenGeneration.description", { port })}
        </Typography>
        {errorMessage && (
          <Typography className="text-destructive" data-testid="cli-token-generation-error" variant="standard">
            {errorMessage}
          </Typography>
        )}
        <Button data-testid="cli-token-generation-confirm" loading={generating} onClick={handleGenerate}>
          {t("pages.cliTokenGeneration.confirmButton")}
        </Button>
      </>
    );
  };

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background p-6">
      <div className="flex max-w-160 flex-col items-center gap-4 text-center">{renderContent()}</div>
    </div>
  );
};
